'use client';

import { useEffect, useState } from 'react';

import { snapshotToDataWithId } from '@ps/functions/utils/snapshotToDataWithId';
import type { WithID } from '@ps/models/lib/base';
import type { Profile } from '@ps/models/lib/profile';

/**
 * One-shot client read of a creator profile (`profiles/{creatorId}`) — for
 * surfaces that only hold the creator id but need the full profile for
 * `SubscribeDialog` (banner, name). Lazy-imports firestore so the SDK stays off
 * the first-load bundle (same deferred pattern as `UserProfileProvider`). `null`
 * while loading; consumers keep the CTA inert until it resolves.
 */
export function useCreatorProfile(creatorId: string): WithID<Profile> | null {
  const [profile, setProfile] = useState<WithID<Profile> | null>(null);
  useEffect(() => {
    let disposed = false;
    void (async () => {
      const { doc, firestore, getDoc } =
        await import('@ps/functions/firebase/firestore');
      if (disposed) return;
      const db = firestore();
      if (!db) return;
      try {
        const snap = await getDoc(doc(db, 'profiles', creatorId));
        if (disposed) return;
        setProfile(snapshotToDataWithId<Profile>(snap) ?? null);
      } catch (error) {
        console.error(
          '[useCreatorProfile] load creator profile failed:',
          error,
        );
      }
    })();
    return () => {
      disposed = true;
    };
  }, [creatorId]);
  return profile;
}
